import { ProductPageTemplate } from "@/components/ProductPageTemplate";
import { Shield, Users, FileText, Clock, Award, Scale } from "lucide-react";
import {getImageUrl} from '@/lib/utils';

const ProfessionalIndemnity = () => {
  const pageData = {
    // SEO Meta Data
    title: "Professional Indemnity Insurance UAE | PI Insurance Dubai | YallaCompare",
    metaDescription: "Get professional indemnity insurance in UAE. Protect your business from professional liability claims in Dubai, Abu Dhabi & UAE. Compare PI insurance quotes online.",

    // Hero Section
    heroTitle: "Professional Indemnity Insurance for UAE Professionals",
    heroSubtitle: "Professional Liability Protection",
    heroDescription: "Protect your business from professional liability claims and errors & omissions. Essential coverage for consultants, advisors, and service providers in the UAE.",
    heroImage: getImageUrl("professional-indemnity-icon.png"),

    // Key Benefits
    keyBenefits: [
      "Protection against professional negligence claims",
      "Coverage for legal fees and defense costs",
      "Safeguard your professional reputation",
      "Meet regulatory and client requirements",
      "Worldwide coverage for global services",
      "Retroactive coverage for past work"
    ],

    // Features Section
    features: [
      {
        title: "Professional Liability Coverage",
        description: "Protection against claims of professional negligence, errors, or omissions in your services and advice.",
        icon: <Shield className="h-6 w-6 text-blue-600" />
      },
      {
        title: "Legal Defense Costs",
        description: "Coverage for legal fees, court costs, and defense expenses for professional liability claims.",
        icon: <Scale className="h-6 w-6 text-blue-600" />
      },
      {
        title: "Reputation Protection",
        description: "Safeguard your professional reputation and business relationships from damaging claims.",
        icon: <Award className="h-6 w-6 text-blue-600" />
      },
      {
        title: "Regulatory Compliance",
        description: "Meet professional body requirements and client contract obligations in the UAE.",
        icon: <FileText className="h-6 w-6 text-blue-600" />
      },
      {
        title: "Worldwide Coverage",
        description: "Protection for services provided globally, not just within the UAE.",
        icon: <Users className="h-6 w-6 text-blue-600" />
      },
      {
        title: "Retroactive Coverage",
        description: "Protection for past work and services provided before policy inception.",
        icon: <Clock className="h-6 w-6 text-blue-600" />
      }
    ],

    // Coverage Details
    coverageDetails: [
      {
        title: "Professional Liability Coverage",
        description: "Comprehensive protection against professional negligence and errors",
        included: [
          "Professional negligence claims",
          "Errors and omissions in services",
          "Breach of professional duty",
          "Defamation and libel claims",
          "Breach of confidentiality"
        ]
      },
      {
        title: "Legal Protection",
        description: "Complete legal defense and cost coverage",
        included: [
          "Legal defense costs and fees",
          "Court costs and settlements",
          "Crisis management expenses",
          "Regulatory investigation costs",
          "Expert witness fees"
        ]
      }
    ],

    // Why Choose Us
    whyChooseUs: [
      {
        title: "Financial Protection",
        description: "Protect your business from potentially devastating financial claims and legal costs"
      },
      {
        title: "Client Confidence",
        description: "Demonstrate professionalism and reliability to clients who require PI insurance"
      },
      {
        title: "Regulatory Requirements",
        description: "Meet professional licensing and regulatory requirements in the UAE"
      },
      {
        title: "Business Continuity",
        description: "Ensure your business can continue operating despite professional liability claims"
      }
    ],

    // FAQ Section
    faqs: [
      {
        question: "Who needs professional indemnity insurance in the UAE?",
        answer: "Consultants, advisors, architects, engineers, IT professionals, accountants, lawyers, and any service provider offering professional advice or services should consider PI insurance."
      },
      {
        question: "What is the difference between professional indemnity and general liability?",
        answer: "Professional indemnity covers claims related to professional services and advice, while general liability covers bodily injury and property damage claims."
      },
      {
        question: "How much professional indemnity coverage do I need?",
        answer: "Coverage amounts depend on your profession, client requirements, and potential claim values. Common limits range from AED 500,000 to AED 10 million or more."
      },
      {
        question: "Does PI insurance cover past work?",
        answer: "Yes, policies typically include retroactive coverage for work performed before the policy start date, subject to terms and conditions."
      },
      {
        question: "Are legal costs covered separately?",
        answer: "Legal defense costs are typically covered in addition to the policy limit, providing extra protection for defense expenses."
      }
    ],

    // CTA Section
    ctaTitle: "Protect Your Professional Practice Today",
    ctaDescription: "Get comprehensive professional indemnity insurance tailored to your profession. Compare quotes from leading UAE insurers and secure your coverage in minutes.",
    primaryCta: "Get PI Insurance Quote",
    secondaryCta: "Compare All Insurance Products",

    // Product ID for quote form
    productId: "professional-indemnity"
  };

  return <ProductPageTemplate {...pageData} />;
};

export default ProfessionalIndemnity;
